import { useState } from "react";
import { Modal } from "@/components/Modal";
import { ErrorBanner } from "@/components/Feedback";
import { useCreate, useUpdate, useList } from "@/hooks/crud";
import { PIPELINE_STAGES } from "@/lib/enums";
import type { Artist, Project, Track } from "@/types/database";

interface Props {
  track?: Track | null;
  onClose: () => void;
  onSaved?: (t: Track) => void;
}

const numOrNull = (v: string): number | null => {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export function TrackForm({ track, onClose, onSaved }: Props) {
  const editing = !!track;
  const artists = useList("artists", "select=id,name&order=name");
  const projects = useList("projects", "select=id,name,artist_id&order=name");

  const [title, setTitle] = useState(track?.title ?? "");
  const [artistId, setArtistId] = useState<string>(
    track?.artist_id ? String(track.artist_id) : "",
  );
  const [projectId, setProjectId] = useState<string>(
    track?.project_id ? String(track.project_id) : "",
  );
  const [stage, setStage] = useState(track?.pipeline_stage ?? "CONCEPT");
  const [epName, setEpName] = useState(track?.ep_name ?? "");
  const [genre, setGenre] = useState(track?.primary_genre ?? "");
  const [bpm, setBpm] = useState(track?.bpm != null ? String(track.bpm) : "");
  const [key, setKey] = useState(track?.key_signature ?? "");
  const [isRemix, setIsRemix] = useState(track?.is_remix ?? false);
  const [notes, setNotes] = useState(track?.notes ?? "");

  const create = useCreate("tracks");
  const update = useUpdate("tracks");
  const mutation = editing ? update : create;

  const artistList = (artists.data?.rows ?? []) as Artist[];
  const projectList = ((projects.data?.rows ?? []) as Project[]).filter(
    (p) => !artistId || p.artist_id === Number(artistId),
  );

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !artistId) return;
    const body = {
      title: title.trim(),
      artist_id: Number(artistId),
      project_id: projectId ? Number(projectId) : null,
      pipeline_stage: stage,
      ep_name: epName.trim() || null,
      primary_genre: genre.trim() || null,
      bpm: numOrNull(bpm),
      key_signature: key.trim() || null,
      is_remix: isRemix,
      notes: notes.trim() || null,
    };
    const saved = editing
      ? await update.mutateAsync({ id: track!.id, body })
      : await create.mutateAsync({ ...body, source: "manual" });
    onSaved?.(saved);
    onClose();
  }

  return (
    <Modal
      title={editing ? "Edit Track" : "New Track"}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={submit}
            type="button"
            disabled={mutation.isPending || !title.trim() || !artistId}
          >
            {mutation.isPending ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <form onSubmit={submit}>
        {mutation.error && <ErrorBanner error={mutation.error} />}
        <div className="field">
          <label>Title *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </div>
        <div className="field-row">
          <div className="field">
            <label>Artist *</label>
            <select value={artistId} onChange={(e) => setArtistId(e.target.value)}>
              <option value="">— select —</option>
              {artistList.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Sub-project</label>
            <select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">— none —</option>
              {projectList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label>Pipeline stage</label>
            <select value={stage} onChange={(e) => setStage(e.target.value)}>
              {PIPELINE_STAGES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>EP name</label>
            <input value={epName} onChange={(e) => setEpName(e.target.value)} />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label>Primary genre</label>
            <input value={genre} onChange={(e) => setGenre(e.target.value)} />
          </div>
          <div className="field">
            <label>BPM</label>
            <input
              value={bpm}
              onChange={(e) => setBpm(e.target.value)}
              inputMode="numeric"
            />
          </div>
        </div>
        <div className="field-row">
          <div className="field">
            <label>Key signature</label>
            <input value={key} onChange={(e) => setKey(e.target.value)} />
          </div>
          <div className="field" style={{ display: "flex", alignItems: "flex-end" }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={isRemix}
                onChange={(e) => setIsRemix(e.target.checked)}
                style={{ width: "auto" }}
              />
              Remix
            </label>
          </div>
        </div>
        <div className="field">
          <label>Notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </form>
    </Modal>
  );
}
